/**
 * Preview-side mirror of the backend's in-box text fitting
 * (`FilledPDFService._place_text_on_page` in backend/turnus_ai/services/filled_pdf_service.py).
 *
 * The interactive answer-review overlay draws answers into the detected cell just like the final,
 * server-rendered PDF. The backend does NOT estimate fit — it calls PyMuPDF `insert_textbox` with
 * Helvetica, which actually lays the text out and reports whether it truly fits, shrinking the font
 * by 0.5 until it does (and truncating with an ellipsis as a last resort). To match that in the
 * browser we do the SAME probe-down loop, but the fit check MEASURES the real rendered text with an
 * offscreen canvas in the same font the PDF uses (Helvetica) instead of guessing from character
 * counts. Measuring the actual font — rather than approximating glyph widths — is what keeps the
 * interactive preview from clipping text that the downloaded PDF fits perfectly.
 *
 * All maths is in PDF points (scale-independent); the caller multiplies by points-to-pixels and zoom
 * when rendering. Widths are measured at the point size directly — only the ratio of text width to
 * box width matters, and both share the same unit, so zoom cancels out.
 */

// The server-rendered PDF draws answer text in Helvetica (PyMuPDF 'helv'). The preview paints AND
// measures in the same font so the interactive fit matches the downloaded PDF: Helvetica where
// available, Arial (metrically near-identical) as the cross-platform fallback. Imported by
// PdfHighlightBox for the actual render so paint and measurement never diverge.
export const PDF_ANSWER_FONT_FAMILY = "Helvetica, Arial, sans-serif"

// Minimum readable font size, matching backend MIN_FONT_SIZE.
export const PDF_MIN_FONT_SIZE = 6
// Font shrink step, matching backend font_step.
const FONT_STEP = 0.5
// Line-height factor: matches the CSS lineHeight (1.2) applied to the preview cell, so the line
// count we compute is the number of lines the browser will actually stack.
const LINE_HEIGHT_FACTOR = 1.2
// Ellipsis-truncating a very short answer produces garbage: "ja" → "j..." is both longer and less
// readable than the original, and short/boolean answers ("Ja", "Nein", "X", "5") must never be
// mangled that way. Below this length we skip truncation entirely and render at the minimum size,
// letting the cell clip if the box is genuinely too small (a clipped "ja" still reads as "ja").
const MIN_LENGTH_TO_TRUNCATE = 6

export type FittedAnswer = {
  /** Font size in points that makes the (possibly truncated) text fit the box. */
  fontSize: number
  /** Text to render — identical to the input unless it had to be ellipsis-truncated to fit. */
  text: string
}

/**
 * Measures the rendered width (in PDF points) of `text` at `fontSize` (PDF points) in the answer
 * font. Injectable so unit tests can supply a deterministic measurer — jsdom has no real canvas
 * text metrics, and real browsers always use the canvas measurer below.
 */
export type MeasureWidth = (text: string, fontSize: number) => number

// Lazily-created shared offscreen canvas 2d context. `undefined` = not tried yet, `null` = no canvas
// available (SSR / jsdom). Reused across every measurement — creating one canvas, not one per call.
let _measureCtx: CanvasRenderingContext2D | null | undefined
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
  if (_measureCtx !== undefined) return _measureCtx
  _measureCtx =
    typeof document !== "undefined"
      ? document.createElement("canvas").getContext("2d")
      : null
  return _measureCtx
}

// Rough fixed-width fallback (~Helvetica average glyph width) for environments without canvas text
// metrics (SSR / jsdom). Real browsers always take the measured path above.
const FALLBACK_AVG_CHAR_WIDTH = 0.5
// 1 CSS pt = 1 PDF pt (both 1/72 in) but measureText returns CSS px (1/96 in). Dividing by this
// ratio converts the canvas pixel measurement back to PDF points.
const CSS_PX_PER_PT = 96 / 72

/** Default production measurer: real Helvetica glyph widths via an offscreen canvas. */
const canvasMeasureWidth: MeasureWidth = (text, fontSize) => {
  const ctx = getMeasureCtx()
  if (ctx) {
    ctx.font = `${fontSize}pt ${PDF_ANSWER_FONT_FAMILY}`
    const { width } = ctx.measureText(text)
    const widthInPoints = width / CSS_PX_PER_PT
    if (widthInPoints > 0 || text.length === 0) return widthInPoints
  }
  return text.length * fontSize * FALLBACK_AVG_CHAR_WIDTH
}

/**
 * Dynamic padding by box height, matching the backend:
 * tiny boxes get no padding (the font needs ~1.2x its size in height, leaving no room),
 * small boxes get 1pt, everything else 2pt.
 */
const paddingForHeight = (height: number): number => {
  const minHeightForPadding = PDF_MIN_FONT_SIZE * 1.5 + 4 // 13
  if (height <= minHeightForPadding) return 0
  if (height <= minHeightForPadding + 4) return 1
  return 2
}

/**
 * Count how many lines `text` wraps to in a `maxWidth`-wide cell at `fontSize`, replicating the
 * browser's (and PyMuPDF's) greedy word-wrap: fill a line word by word until the next word would
 * overflow, then start a new line. A single word wider than the cell is broken mid-word, matching
 * the CSS `overflow-wrap: break-word` on the preview cell. Explicit newlines start new lines.
 */
const countWrappedLines = (
  measure: MeasureWidth,
  text: string,
  fontSize: number,
  maxWidth: number,
): number => {
  let lines = 0
  for (const paragraph of text.split("\n")) {
    if (paragraph.length === 0) {
      lines += 1 // an explicit blank line still occupies a row
      continue
    }
    let line = ""
    for (const rawWord of paragraph.split(" ")) {
      let word = rawWord
      const candidate = line === "" ? word : `${line} ${word}`
      if (measure(candidate, fontSize) <= maxWidth) {
        line = candidate
        continue
      }
      // Adding `word` overflows the current line: commit what we have and start fresh.
      if (line !== "") lines += 1
      // `word` alone may still be wider than the cell — break it mid-word (CSS overflow-wrap).
      while (word.length > 1 && measure(word, fontSize) > maxWidth) {
        let fit = 1
        while (
          fit < word.length &&
          measure(word.slice(0, fit + 1), fontSize) <= maxWidth
        ) {
          fit += 1
        }
        lines += 1
        word = word.slice(fit)
      }
      line = word
    }
    lines += 1 // the final (or only) line of this paragraph
  }
  return Math.max(1, lines)
}

/**
 * Mirror of backend `_sanitize_for_helv`: replace characters outside Helvetica's PDFDocEncoding
 * with ASCII equivalents so the preview measures and draws exactly what the PDF renders.
 */
const sanitizeForHelv = (t: string): string =>
  t
    .replace(/€/g, "EUR")
    .replace(/‘/g, "'") // left single quotation mark
    .replace(/’/g, "'") // right single quotation mark
    .replace(/“/g, '"') // left double quotation mark
    .replace(/”/g, '"') // right double quotation mark
    .replace(/–/g, "-") // en dash
    .replace(/—/g, "--") // em dash
    .replace(/…/g, "...") // horizontal ellipsis
    .replace(/\u00A0/g, " ") // non-breaking space
    .replace(/•/g, "-") // bullet point
    .replace(/[✓✔☑✅]/g, "X") // check-marks (no helv glyph) -> X, matching the drawn checkbox

/**
 * Fit `text` into a `boxWidth` x `boxHeight` (points) cell starting from `initialFontSize`,
 * mirroring the backend's `insert_textbox` probe-down. Sanitizes the text first (matching
 * `_sanitize_for_helv`) so measurement and the returned display text use the PDF-equivalent string.
 * Returns the chosen font size and the text to draw (ellipsis-truncated only when even the minimum
 * font cannot fit). `measureWidth` is injectable for tests; production measures real Helvetica.
 *
 * Empty text is returned unchanged at the initial size. Non-positive box dimensions are floored to
 * 1pt so the fit stays well-defined (a degenerate box yields the minimum font rather than NaN).
 */
export const fitAnswerText = (
  text: string,
  boxWidth: number,
  boxHeight: number,
  initialFontSize: number,
  measureWidth: MeasureWidth = canvasMeasureWidth,
): FittedAnswer => {
  if (!text) return { fontSize: initialFontSize, text }
  const sanitized = sanitizeForHelv(text)

  const padding = paddingForHeight(boxHeight)
  const width = Math.max(1, boxWidth - 2 * padding)
  const height = Math.max(1, boxHeight - 2 * padding)

  const fitsAt = (fs: number): boolean => {
    const lineHeight = fs * LINE_HEIGHT_FACTOR
    // A font fits only if a single line genuinely fits the box HEIGHT. The previous
    // Math.max(1, Math.floor(height / lineHeight)) forced "one line is allowed" even when that
    // line was TALLER than the box, so thin cells (e.g. the ~8pt IOM rows) kept an oversized
    // font that the box then clipped — the "choppy" look. Rejecting a line taller than the box
    // mirrors PyMuPDF insert_textbox, which truly fails and shrinks the font down.
    if (lineHeight > height) return false
    const maxLines = Math.floor(height / lineHeight)
    return countWrappedLines(measureWidth, sanitized, fs, width) <= maxLines
  }

  // 1) Shrink the font until the whole answer fits — the same probe-down as the backend's
  //    insert_textbox loop, but the fit check measures real Helvetica text instead of guessing.
  let fontSize = initialFontSize
  while (fontSize >= PDF_MIN_FONT_SIZE) {
    if (fitsAt(fontSize)) return { fontSize, text: sanitized }
    fontSize -= FONT_STEP
  }

  // 2) The whole answer already fits ONE line at the minimum font: keep it whole. The shrink loop
  //    only failed on height (the box is shorter than a single min-size line — e.g. a sub-7.2pt
  //    cell), so let the overlay clip vertically instead of ellipsis-truncating text that fits the
  //    width. Mirrors the backend baseline fallback, which draws the full width-fitting line.
  if (
    countWrappedLines(measureWidth, sanitized, PDF_MIN_FONT_SIZE, width) <= 1
  ) {
    return { fontSize: PDF_MIN_FONT_SIZE, text: sanitized }
  }

  // 3) Very short answers are never worth ellipsis-truncating — "ja" → "j..." is worse than a
  //    clipped "ja". Render at the minimum size and let the cell clip (see MIN_LENGTH_TO_TRUNCATE).
  if (sanitized.length < MIN_LENGTH_TO_TRUNCATE) {
    return { fontSize: PDF_MIN_FONT_SIZE, text: sanitized }
  }

  // 4) Still too long at the minimum size: truncate with an ellipsis until what remains fits,
  //    dropping ~15% of the characters each pass (matching the backend's 0.85 ratio).
  const minMaxLines = Math.max(
    1,
    Math.floor(height / (PDF_MIN_FONT_SIZE * LINE_HEIGHT_FACTOR)),
  )
  let trimmed = sanitized
  while (trimmed.length > 1) {
    trimmed = trimmed
      .slice(0, Math.max(1, Math.floor(trimmed.length * 0.85)))
      .trimEnd()
    if (
      countWrappedLines(
        measureWidth,
        `${trimmed}...`,
        PDF_MIN_FONT_SIZE,
        width,
      ) <= minMaxLines
    ) {
      return { fontSize: PDF_MIN_FONT_SIZE, text: `${trimmed}...` }
    }
  }

  // 5) Even a single character + ellipsis will not fit: render the sanitized text at the minimum
  //    size (the box CSS clips the overflow), matching the backend's final give-up branch.
  return { fontSize: PDF_MIN_FONT_SIZE, text: sanitized }
}
